import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  Play, 
  Pause, 
  RotateCcw,
  Wind,
  Clock,
  CheckCircle,
  Heart,
  Droplets,
  Moon,
  Smartphone,
  Utensils,
  Pencil
} from "lucide-react";
import { toast } from "sonner";

// ==================== BREATHING EXERCISE ====================
export const BreathingExercise = ({ onComplete, onClose }) => {
  const [phase, setPhase] = useState("idle"); // idle, inhale, hold, exhale, complete
  const [cycle, setCycle] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const totalCycles = 5;
  const timerRef = useRef(null);

  const phases = {
    inhale: { duration: 4, label: "Inhala", color: "from-cyan-400 to-blue-500" },
    hold: { duration: 4, label: "Mantén", color: "from-purple-400 to-indigo-500" },
    exhale: { duration: 6, label: "Exhala", color: "from-pink-400 to-rose-500" },
  };

  const startExercise = () => {
    setIsRunning(true);
    setCycle(0);
    runPhase("inhale");
  };

  const runPhase = (currentPhase) => {
    setPhase(currentPhase);
    
    const duration = phases[currentPhase].duration * 1000;
    
    timerRef.current = setTimeout(() => {
      if (currentPhase === "inhale") {
        runPhase("hold");
      } else if (currentPhase === "hold") {
        runPhase("exhale");
      } else if (currentPhase === "exhale") {
        setCycle(prev => {
          const newCycle = prev + 1;
          if (newCycle >= totalCycles) {
            setPhase("complete");
            setIsRunning(false);
            return newCycle;
          }
          runPhase("inhale");
          return newCycle;
        });
      }
    }, duration);
  };

  const handleComplete = () => {
    onComplete();
    toast.success("¡Ejercicio de respiración completado! 🧘");
  };

  const reset = () => {
    clearTimeout(timerRef.current);
    setPhase("idle");
    setCycle(0);
    setIsRunning(false);
  };

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-void-paper border border-neon-cyan/30 rounded-2xl p-8 max-w-md w-full text-center"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        <Wind className="w-12 h-12 text-neon-cyan mx-auto mb-4" />
        <h2 className="font-orbitron text-xl text-white mb-2">Respiración Profunda</h2>
        <p className="text-slate-400 text-sm mb-6">10 respiraciones conscientes</p>

        {/* Breathing circle */}
        <div className="relative w-48 h-48 mx-auto mb-6">
          <motion.div
            className={`absolute inset-0 rounded-full bg-gradient-to-br ${
              phase !== "idle" && phase !== "complete" 
                ? phases[phase]?.color 
                : "from-slate-600 to-slate-700"
            }`}
            animate={{
              scale: phase === "inhale" ? 1.3 : phase === "exhale" ? 0.8 : 1,
            }}
            transition={{ duration: phase !== "idle" && phase !== "complete" ? phases[phase]?.duration : 0.3 }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white font-orbitron text-lg">
              {phase === "idle" && "Preparado"}
              {phase === "complete" && "¡Completo!"}
              {phase !== "idle" && phase !== "complete" && phases[phase]?.label}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="flex justify-center gap-2 mb-6">
          {Array.from({ length: totalCycles }).map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-colors ${
                i < cycle ? "bg-green-400" : "bg-slate-600"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        {phase === "idle" && (
          <button
            onClick={startExercise}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-neon-cyan/20 border border-neon-cyan/50 text-neon-cyan hover:bg-neon-cyan/30 transition-all font-rajdhani font-semibold"
          >
            <Play className="w-5 h-5" />
            Comenzar
          </button>
        )}

        {isRunning && (
          <button
            onClick={reset}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all font-rajdhani"
          >
            <RotateCcw className="w-5 h-5" />
            Reiniciar
          </button>
        )}

        {phase === "complete" && (
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 transition-all font-rajdhani font-semibold"
          >
            <CheckCircle className="w-5 h-5" />
            ¡Completar Reto!
          </button>
        )}
      </motion.div>
    </motion.div>
  );
};

// ==================== MEDITATION TIMER ====================
export const MeditationTimer = ({ duration = 5, onComplete, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsComplete(true);
            clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleComplete = () => {
    onComplete();
    toast.success("¡Meditación completada! 🧘‍♀️");
  };

  const progress = ((duration * 60 - timeLeft) / (duration * 60)) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-void-paper border border-neon-purple/30 rounded-2xl p-8 max-w-md w-full text-center"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        <Moon className="w-12 h-12 text-neon-purple mx-auto mb-4" />
        <h2 className="font-orbitron text-xl text-white mb-2">Meditación</h2>
        <p className="text-slate-400 text-sm mb-6">{duration} minutos de calma</p>

        {/* Timer circle */}
        <div className="relative w-48 h-48 mx-auto mb-6">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="96"
              cy="96"
              r="88"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-slate-700"
            />
            <motion.circle
              cx="96"
              cy="96"
              r="88"
              stroke="url(#gradient)"
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={553}
              animate={{ strokeDashoffset: 553 - (553 * progress) / 100 }}
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#A855F7" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white font-orbitron text-3xl">
              {isComplete ? "✓" : formatTime(timeLeft)}
            </span>
          </div>
        </div>

        {/* Actions */}
        {!isComplete ? (
          <button
            onClick={() => setIsRunning(!isRunning)}
            className={`flex items-center gap-2 mx-auto px-6 py-3 rounded-xl border transition-all font-rajdhani font-semibold ${
              isRunning
                ? "bg-orange-500/20 border-orange-500/50 text-orange-400"
                : "bg-neon-purple/20 border-neon-purple/50 text-neon-purple"
            }`}
          >
            {isRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isRunning ? "Pausar" : "Comenzar"}
          </button>
        ) : (
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 transition-all font-rajdhani font-semibold"
          >
            <CheckCircle className="w-5 h-5" />
            ¡Completar Reto!
          </button>
        )}

        <p className="text-xs text-slate-500 mt-4">
          Encuentra un lugar tranquilo y respira profundamente
        </p>
      </motion.div>
    </motion.div>
  );
};

// ==================== WATER TRACKER ====================
export const WaterTracker = ({ target = 8, onComplete, onClose }) => {
  const [glasses, setGlasses] = useState(0);

  const addGlass = () => {
    if (glasses < target) {
      setGlasses(prev => prev + 1);
    }
  };

  const handleComplete = () => {
    if (glasses >= target) {
      onComplete();
      toast.success("¡Meta de hidratación alcanzada! 💧");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-void-paper border border-cyan-500/30 rounded-2xl p-8 max-w-md w-full text-center"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        <Droplets className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
        <h2 className="font-orbitron text-xl text-white mb-2">Hidratación</h2>
        <p className="text-slate-400 text-sm mb-6">Bebe {target} vasos de agua hoy</p>

        {/* Water glasses */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {Array.from({ length: target }).map((_, i) => (
            <motion.button
              key={i}
              onClick={i === glasses ? addGlass : undefined}
              whileHover={i === glasses ? { scale: 1.1 } : {}}
              whileTap={i === glasses ? { scale: 0.95 } : {}}
              className={`w-12 h-16 mx-auto rounded-lg border-2 flex items-end justify-center overflow-hidden transition-all ${
                i < glasses
                  ? "border-cyan-400 bg-cyan-400/20"
                  : i === glasses
                  ? "border-cyan-400 cursor-pointer hover:bg-cyan-400/10"
                  : "border-slate-600"
              }`}
            >
              {i < glasses && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "100%" }}
                  className="w-full bg-gradient-to-t from-cyan-500 to-cyan-300"
                />
              )}
            </motion.button>
          ))}
        </div>

        <p className="text-2xl font-orbitron text-white mb-4">
          {glasses} / {target} vasos
        </p>

        {glasses >= target ? (
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 transition-all font-rajdhani font-semibold"
          >
            <CheckCircle className="w-5 h-5" />
            ¡Completar Reto!
          </button>
        ) : (
          <p className="text-sm text-slate-400">
            Toca el siguiente vaso cada vez que bebas agua
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};

// ==================== DIGITAL DETOX TIMER ====================
export const DigitalDetoxTimer = ({ duration = 60, onComplete, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsComplete(true);
            clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleComplete = () => {
    onComplete();
    toast.success("¡Desconexión digital completada! 📵");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-void-paper border border-orange-500/30 rounded-2xl p-8 max-w-md w-full text-center"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        <Smartphone className="w-12 h-12 text-orange-400 mx-auto mb-4" />
        <h2 className="font-orbitron text-xl text-white mb-2">Desconexión Digital</h2>
        <p className="text-slate-400 text-sm mb-6">{duration} minutos sin redes sociales</p>

        <div className="text-5xl font-orbitron text-white mb-6">
          {isComplete ? "✓" : formatTime(timeLeft)}
        </div>

        {!isComplete ? (
          <>
            <button
              onClick={() => setIsRunning(!isRunning)}
              className={`flex items-center gap-2 mx-auto px-6 py-3 rounded-xl border transition-all font-rajdhani font-semibold ${
                isRunning
                  ? "bg-red-500/20 border-red-500/50 text-red-400"
                  : "bg-orange-500/20 border-orange-500/50 text-orange-400"
              }`}
            >
              {isRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              {isRunning ? "Pausar" : "Comenzar"}
            </button>
            <p className="text-xs text-slate-500 mt-4">
              Deja el teléfono y haz algo que disfrutes
            </p>
          </>
        ) : (
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 transition-all font-rajdhani font-semibold"
          >
            <CheckCircle className="w-5 h-5" />
            ¡Completar Reto!
          </button>
        )}
      </motion.div>
    </motion.div>
  );
};

// ==================== AFFIRMATION EXERCISE ====================
export const AffirmationExercise = ({ onComplete, onClose }) => {
  const [current, setCurrent] = useState(0);
  const [completed, setCompleted] = useState([false, false, false]);

  const affirmations = [
    "Soy capaz de lograr todo lo que me propongo",
    "Merezco amor, respeto y felicidad",
    "Cada día soy una mejor versión de mí mismo/a"
  ];

  const markComplete = () => {
    const newCompleted = [...completed];
    newCompleted[current] = true;
    setCompleted(newCompleted);

    if (current < 2) {
      setCurrent(current + 1);
    }
  };

  const allComplete = completed.every(c => c);

  const handleComplete = () => {
    onComplete();
    toast.success("¡Afirmaciones completadas! 💪");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-void-paper border border-pink-500/30 rounded-2xl p-8 max-w-md w-full text-center"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        <Heart className="w-12 h-12 text-pink-400 mx-auto mb-4" />
        <h2 className="font-orbitron text-xl text-white mb-2">Afirmaciones Positivas</h2>
        <p className="text-slate-400 text-sm mb-6">Di estas 3 afirmaciones en voz alta</p>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {completed.map((c, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-colors ${
                c ? "bg-green-400" : i === current ? "bg-pink-400" : "bg-slate-600"
              }`}
            />
          ))}
        </div>

        {/* Current affirmation */}
        <motion.div
          key={current}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl bg-pink-500/10 border border-pink-500/30 mb-6"
        >
          <p className="text-xl text-white font-light italic">
            &ldquo;{affirmations[current]}&rdquo;
          </p>
        </motion.div>

        {!allComplete ? (
          <button
            onClick={markComplete}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-pink-500/20 border border-pink-500/50 text-pink-400 hover:bg-pink-500/30 transition-all font-rajdhani font-semibold"
          >
            <CheckCircle className="w-5 h-5" />
            ¡Lo dije!
          </button>
        ) : (
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 transition-all font-rajdhani font-semibold"
          >
            <CheckCircle className="w-5 h-5" />
            ¡Completar Reto!
          </button>
        )}
      </motion.div>
    </motion.div>
  );
};

// ==================== CHALLENGE ACTIVITY ROUTER ====================
export const ChallengeActivity = ({ challengeId, onComplete, onClose }) => {
  // Map challenge IDs to their interactive components
  const activityMap = {
    deep_breathing: BreathingExercise,
    meditate_5min: MeditationTimer,
    hydrate: WaterTracker,
    digital_detox: DigitalDetoxTimer,
    self_affirmation: AffirmationExercise,
  };

  const ActivityComponent = activityMap[challengeId];

  if (!ActivityComponent) {
    return null; // No interactive activity for this challenge
  }

  return (
    <AnimatePresence>
      <ActivityComponent onComplete={onComplete} onClose={onClose} />
    </AnimatePresence>
  );
};

export default ChallengeActivity;
